import { useMutation, useQuery } from '@apollo/client';
import { Formik, FormikValues } from 'formik';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { UPDATE_USER_PROFILE } from '../../../../graphql/UPDATE_USER_PROFILE';
import { CREATE_OR_UPDATE_BRAND, GET_BRAND } from '../../../../graphql/BRAND';
import { GET_CURRENT_USER } from '../../../../graphql/GET_CURRENT_USER';
import BrandNameStep from './brand-steps/NameStep';
import BrandNameInputStep from './brand-steps/BrandNameStep';
import BrandEmailStep from './brand-steps/EmailStep';
import BrandLogoUploadStep from './brand-steps/LogoUploadStep';
import BrandInfoStep from './brand-steps/InfoStep';
import BrandCalendlyStep from './brand-steps/CalendlyStep';
import BrandProgressBar from './ProgressBar';
import BrandMascotCharacter from './BrandMascotCharacter';
import './onboarding.sass';

const STEPS = {
  NAME: 0,
  BRAND: 1,
  EMAIL: 2,
  LOGO: 3,
  INFO: 4,
  CALENDLY: 5
};

const getBadgeText = (step: number) => {
  switch (step) {
    case STEPS.NAME:
      return 'Brand Information';
    case STEPS.BRAND:
    case STEPS.EMAIL:
    case STEPS.LOGO:
    case STEPS.INFO:
    case STEPS.CALENDLY:
      return 'Optional';
    default:
      return 'Brand Information';
  }
};

const getBadgeClass = (step: number) => {
  return step === STEPS.BRAND ||
    step === STEPS.EMAIL ||
    step === STEPS.LOGO ||
    step === STEPS.INFO ||
    step === STEPS.CALENDLY
    ? 'badge-optional'
    : '';
};

const BrandOnboardingForm: React.FunctionComponent = () => {
  const [currentStep, setCurrentStep] = useState(STEPS.NAME);
  const [initialValues, setInitialValues] = useState({
    firstName: '',
    lastName: '',
    brandName: '',
    businessEmail: '',
    brandLogo: '',
    brandInfo: '',
    brandTags: []
  });

  // Fetch current user data
  const { data: userData, loading: userLoading } = useQuery(GET_CURRENT_USER);

  // Fetch existing brand data
  const { data: brandData, loading: brandLoading } = useQuery(GET_BRAND);

  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE, {
    onError: (error) => {
      console.error('User profile update error:', error);
      toast.error('Failed to update user profile. Please try again.');
    }
  });

  const [createOrUpdateBrand] = useMutation(CREATE_OR_UPDATE_BRAND, {
    onError: (error) => {
      console.error('Brand profile update error:', error);
      toast.error('Failed to update brand profile. Please try again.');
    }
  });

  // Populate initial values when data is loaded
  useEffect(() => {
    if (userData?.me || brandData?.getBrand) {
      setInitialValues({
        firstName: userData?.me?.profile?.firstName || '',
        lastName: userData?.me?.profile?.lastName || '',
        brandName: brandData?.getBrand?.brandName || '',
        businessEmail: brandData?.getBrand?.businessEmail || '',
        brandLogo: brandData?.getBrand?.logoUrl || '',
        brandInfo: brandData?.getBrand?.description || '',
        brandTags: brandData?.getBrand?.tags || []
      });
    }
  }, [userData, brandData]);

  const goToNextStep = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const renderStepContent = (formikProps: FormikValues) => {
    const {
      handleSubmit,
      values,
      setFieldTouched
    } = formikProps;

    const handleNext = async (fieldsToValidate?: string | string[], stepType?: string) => {
      // Validate field if provided
      if (fieldsToValidate) {
        const fields = Array.isArray(fieldsToValidate) ? fieldsToValidate : [fieldsToValidate];
        let hasError = false;

        for (const field of fields) {
          setFieldTouched(field, true);
        }

        const currentErrors = await formikProps.validateForm();

        for (const field of fields) {
          if (currentErrors[field]) {
            hasError = true;
          }
        }

        if (hasError) return;
      }

      // Save data based on current step
      try {
        switch (stepType) {
          case 'NAME':
            if (values.firstName && values.lastName) {
              await updateUserProfile({
                variables: {
                  firstName: values.firstName,
                  lastName: values.lastName
                },
                fetchPolicy: 'no-cache'
              });
            }
            break;

          case 'BRAND':
            if (values.brandName) {
              await createOrUpdateBrand({
                variables: {
                  brandData: { brandName: values.brandName }
                },
                fetchPolicy: 'no-cache'
              });
            }
            break;

          case 'EMAIL':
            if (values.businessEmail) {
              await createOrUpdateBrand({
                variables: {
                  brandData: { businessEmail: values.businessEmail }
                },
                fetchPolicy: 'no-cache'
              });
            }
            break;

          case 'LOGO':
            if (values.brandLogo) {
              await createOrUpdateBrand({
                variables: {
                  brandData: { logoUrl: values.brandLogo }
                },
                fetchPolicy: 'no-cache'
              });
            }
            break;

          case 'INFO':
            if (values.brandInfo || values.brandTags?.length > 0) {
              await createOrUpdateBrand({
                variables: {
                  brandData: {
                    description: values.brandInfo,
                    tags: values.brandTags
                  }
                },
                fetchPolicy: 'no-cache'
              });
            }
            break;

          default:
            break;
        }

        goToNextStep();
      } catch (error) {
        console.error('Error saving step data:', error);
        toast.error('Failed to save. Please try again.');
      }
    };

    const handleFormSubmit = () => {
      console.log('Submitting brand onboarding form with values:', values);
      handleSubmit();
    };

    switch (currentStep) {
      case STEPS.NAME:
        return (
          <BrandNameStep
            formik={formikProps}
            goToNextStep={async (field) => await handleNext(field, 'NAME')}
          />
        );

      case STEPS.BRAND:
        return (
          <BrandNameInputStep
            formik={formikProps}
            goToNextStep={async (field) => await handleNext(field, 'BRAND')}
            skipStep={() => goToNextStep()}
          />
        );

      case STEPS.EMAIL:
        return (
          <BrandEmailStep
            formik={formikProps}
            goToNextStep={async (field) => await handleNext(field, 'EMAIL')}
            skipStep={() => goToNextStep()}
          />
        );

      case STEPS.LOGO:
        return (
          <BrandLogoUploadStep
            formik={formikProps}
            goToNextStep={async () => await handleNext(undefined, 'LOGO')}
            skipStep={goToNextStep}
          />
        );

      case STEPS.INFO:
        return (
          <BrandInfoStep
            formik={formikProps}
            goToNextStep={async () => await handleNext(undefined, 'INFO')}
            skipStep={goToNextStep}
          />
        );

      case STEPS.CALENDLY:
        return (
          <BrandCalendlyStep
            goToNextStep={handleFormSubmit}
            skipStep={handleFormSubmit}
          />
        );

      default:
        return null;
    }
  };

  const totalSteps = Object.keys(STEPS).length;
  const badgeText = getBadgeText(currentStep);
  const badgeClass = getBadgeClass(currentStep);
  const isLoading = userLoading || brandLoading;

  if (isLoading) {
    return (
      <div className="has-text-centered p-6">
        <progress className="progress is-small is-info" max="100">Loading...</progress>
        <p>Loading your brand information...</p>
      </div>
    );
  }

  return (
    <>
      <div className={`badge-about-you badge-desktop ${badgeClass}`}>
        {badgeText}
      </div>

      <div className="onboarding-container">
        <div className={`badge-about-you badge-mobile ${badgeClass}`}>
          {badgeText}
        </div>
        <div
          className={`mascot-wrapper brand-mascot-wrapper ${currentStep === STEPS.LOGO ? 'mascot-centered' : ''}`}
          style={{
            visibility: (currentStep === STEPS.BRAND || currentStep === STEPS.EMAIL || currentStep === STEPS.INFO || currentStep === STEPS.CALENDLY)
              ? 'hidden'
              : 'visible'
          }}
        >
          <BrandMascotCharacter currentStep={currentStep} totalSteps={totalSteps} />
        </div>
        <div className="onboarding-form-container">
          <BrandProgressBar currentStep={currentStep} totalSteps={totalSteps} />

          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                console.log('Completing brand onboarding...');

                // All data has been saved incrementally during each step
                // Now only mark as onboarded and redirect
                const result = await updateUserProfile({
                  variables: {
                    isOnboarded: true
                  },
                  fetchPolicy: 'no-cache',
                  errorPolicy: 'all'
                });

                if (result.data?.editUserProfile) {
                  console.log('Brand onboarding completed successfully, redirecting...');
                  toast.success('Brand profile setup completed successfully!');
                  setTimeout(() => {
                    window.location.replace('/');
                  }, 1000);
                } else {
                  throw new Error('Failed to mark onboarding as complete');
                }
              } catch (error) {
                console.error('Brand onboarding completion failed:', error);
                toast.error('Failed to complete onboarding. Please try again.');
              } finally {
                setSubmitting(false);
              }
            }}
            validationSchema={Yup.object().shape({
              firstName: Yup.string().required('First name is required'),
              lastName: Yup.string().required('Last name is required'),
              brandName: Yup.string(),
              businessEmail: Yup.string().email('Invalid email format')
            })}
          >
            {(formikProps) => (
              <>
                {renderStepContent(formikProps)}
              </>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default BrandOnboardingForm;